import React, { useEffect, useState } from "react";
import { Button, Divider, Empty, Spin, Tag, Typography } from "@douyinfe/semi-ui";
import { IconChevronDown, IconChevronUp } from "@douyinfe/semi-icons";
import { AiCoachChatApi, ChatTemplate } from "../../../api/aiCoachChat";
import { intentionLevelLabel } from "../../../api/aiCoach";
import { displayLeadStatus } from "../../../api/ApiClient";
import {
  avatar,
  panelHeader,
  scoreBadgeColor,
  scoreBadgeStyle,
  sp,
} from "../styles";

const { Title, Text } = Typography;

interface Props {
  leadId: number | null;
  leadName?: string;
  leadIntention?: string;
  leadStatus?: string;
  leadStar?: number | null;
  leadScore?: number | null;
  leadNo?: string;
  onPickTemplate: (content: string) => void;
}

const AVATAR_COLORS = [
  "#6C5CE7", "#00B894", "#0984E3", "#E17055", "#00CEC9",
  "#FDCB6E", "#E84393", "#636E72", "#2D3436", "#74B9FF",
];

function colorFor(id?: number): string {
  if (id == null) return AVATAR_COLORS[0];
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

const CustomerProfilePanel: React.FC<Props> = ({
  leadId,
  leadName,
  leadIntention,
  leadStatus,
  leadStar,
  leadScore,
  leadNo,
  onPickTemplate,
}) => {
  const [userPrompt, setUserPrompt] = useState("");
  const [promptOpen, setPromptOpen] = useState(false);
  const [templates, setTemplates] = useState<ChatTemplate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (leadId == null) {
      setUserPrompt("");
      setTemplates([]);
      return;
    }
    setLoading(true);
    Promise.all([
      AiCoachChatApi.userPrompt(leadId).then((r) => r.data ?? "").catch(() => ""),
      AiCoachChatApi.listTemplates(leadId).then((r) => r.data ?? []).catch(() => []),
    ])
      .then(([p, t]) => {
        setUserPrompt(p);
        setTemplates(t);
      })
      .finally(() => setLoading(false));
  }, [leadId]);

  if (leadId == null) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty description="选择客户后展示档案" />
      </div>
    );
  }

  const groupedTemplates = templates.reduce<Record<string, ChatTemplate[]>>((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={panelHeader}>
        <Title heading={6} style={{ margin: 0 }}>客户档案</Title>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: sp.md }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: sp.xl }}><Spin /></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: sp.md }}>
            <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
              <div
                style={{
                  ...avatar,
                  width: 48,
                  height: 48,
                  fontSize: 18,
                  background: colorFor(leadId),
                }}
              >
                {(leadName || "?").charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Title heading={6} ellipsis={{ showTooltip: { opts: { content: leadName } } }} style={{ margin: 0 }}>
                  {leadName || `#${leadId}`}
                </Title>
                {leadNo && (
                  <Text type="tertiary" size="small">ID: {leadId}</Text>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs }}>
              {leadScore != null && (
                <span
                  style={{ ...scoreBadgeStyle, background: scoreBadgeColor(leadScore) }}
                  title="AI 评分"
                >
                  {leadScore} 分
                </span>
              )}
              {leadIntention && (
                <Tag size="large" color="white">
                  {intentionLevelLabel(leadIntention)}
                </Tag>
              )}
              {leadStar != null && (
                <Tag size="large" color="amber">{leadStar} ★</Tag>
              )}
              {leadStatus && (
                <Tag size="large" color="blue">{displayLeadStatus(leadStatus)}</Tag>
              )}
            </div>

            <Divider margin={sp.xs} />

            <Section title="UserPrompt">
              {userPrompt ? (
                <>
                  <div
                    style={{
                      padding: sp.sm,
                      background: "var(--semi-color-bg-2)",
                      borderRadius: 6,
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: "var(--semi-color-text-1)",
                      maxHeight: promptOpen ? undefined : 120,
                      overflow: promptOpen ? undefined : "hidden",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      borderLeft: "2px solid #6C5CE7",
                    }}
                  >
                    {userPrompt}
                  </div>
                  <Button
                    size="small"
                    theme="borderless"
                    icon={promptOpen ? <IconChevronUp /> : <IconChevronDown />}
                    onClick={() => setPromptOpen((v) => !v)}
                    style={{ marginTop: sp.xs, paddingLeft: 0 }}
                  >
                    {promptOpen ? "收起" : "展开全部"}
                  </Button>
                </>
              ) : (
                <Text type="tertiary" size="small">暂无注入内容</Text>
              )}
            </Section>

            <Section title="快捷提问">
              {Object.keys(groupedTemplates).length === 0 ? (
                <Text type="tertiary" size="small">暂无模板</Text>
              ) : (
                Object.entries(groupedTemplates).map(([cat, items]) => (
                  <div key={cat} style={{ marginBottom: sp.sm }}>
                    <Text type="tertiary" size="small" strong>{cat}</Text>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs, marginTop: sp.xs }}>
                      {items.map((t) => (
                        <Tag
                          key={t.id}
                          size="large"
                          color="light-blue"
                          style={{ cursor: "pointer" }}
                          onClick={() => onPickTemplate(t.content)}
                        >
                          {t.title}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <Text type="tertiary" size="small" strong style={{ letterSpacing: 0.5 }}>
      {title.toUpperCase()}
    </Text>
    <div style={{ marginTop: sp.xs }}>{children}</div>
  </div>
);

export default CustomerProfilePanel;
